import React from 'react';
import { Link } from 'react-router-dom';
import { TowerControl, Truck, Container, Users, CheckCircle, ArrowRight, Warehouse } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { services } from '../mock';

const iconMap = {
  crane: TowerControl,
  truck: Truck,
  container: Container,
  users: Users,
  warehouse: Warehouse
};

const serviceSlugMap = {
  1: 'lorry-crane',
  2: 'tailgate',
  3: 'trailer',
  4: 'manpower',
  5: 'warehouse-storage'
};

const serviceImages = [
  'https://customer-assets.emergentagent.com/job_193da88a-38a6-4eb7-b1f4-1a973cc5c4bb/artifacts/7gbywntd_image.png',
  'https://customer-assets.emergentagent.com/job_193da88a-38a6-4eb7-b1f4-1a973cc5c4bb/artifacts/2yo3v1hq_WhatsApp%20Image%202026-01-06%20at%201.47.31%20PM.jpeg',
  'https://customer-assets.emergentagent.com/job_193da88a-38a6-4eb7-b1f4-1a973cc5c4bb/artifacts/c00vrclr_image.png',
  'https://customer-assets.emergentagent.com/job_193da88a-38a6-4eb7-b1f4-1a973cc5c4bb/artifacts/cgsbbfqb_WhatsApp%20Image%202026-02-02%20at%2011.01.59%20AM.jpeg',
];
const FALLBACK_SERVICE_IMAGE = 'https://images.unsplash.com/photo-1768796373552-1168cb598645?w=800';

export const Services = () => {
  return (
    <div className="min-h-screen bg-burgundy-deep">
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 bg-burgundy-dark grain-overlay relative">
        <div className="container mx-auto text-center relative z-10">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">
            Our Services
          </h1>
          <p className="text-lg text-white/70 max-w-3xl mx-auto">
            Comprehensive heavy vehicle logistics solutions with professional operators and modern equipment
          </p>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <div className="space-y-16">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon];
              const isEven = index % 2 === 0;
              return (
                <div key={service.id} className={`grid lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:grid-flow-dense' : ''}`}>
                  <div className={isEven ? '' : 'lg:col-start-2'}>
                    <div className="w-16 h-16 bg-gold/15 rounded-xl flex items-center justify-center mb-6">
                      <Icon className="text-gold" size={32} />
                    </div>
                    <h2 className="font-display text-3xl font-bold text-white mb-4">{service.name}</h2>
                    <p className="text-lg text-white/70 mb-6">{service.description}</p>
                    <div className="bg-burgundy-dark border border-gold/20 p-4 rounded-lg mb-6">
                      <p className="text-sm font-semibold text-white/60 mb-2">Capacity Range</p>
                      <p className="text-2xl font-bold text-gold">{service.capacity}</p>
                    </div>
                    <div className="mb-6">
                      <h3 className="font-semibold text-white mb-3">Available Options:</h3>
                      <ul className="space-y-2">
                        {service.details.map((detail, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-white/80">
                            <CheckCircle className="text-gold flex-shrink-0" size={20} />
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="mb-6">
                      <h3 className="font-semibold text-white mb-3">Features:</h3>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-white/80">
                            <CheckCircle className="text-gold flex-shrink-0" size={20} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <Link to={`/services/${serviceSlugMap[service.id]}`}>
                      <Button className="bg-gold hover:bg-gold-light text-burgundy-deep font-semibold group">
                        Learn More
                        <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                      </Button>
                    </Link>
                  </div>
                  <div className={isEven ? 'lg:col-start-2' : ''}>
                    <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                      <img
                        src={serviceImages[index] || FALLBACK_SERVICE_IMAGE}
                        alt={service.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-burgundy-dark grain-overlay relative">
        <div className="container mx-auto">
          <Card className="border border-gold/25 bg-burgundy-deep relative z-10">
            <CardContent className="p-12 text-center">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
                Need a Custom Solution?
              </h2>
              <p className="text-lg text-white/70 mb-8 max-w-2xl mx-auto">
                We can tailor our services to meet your specific requirements. Get in touch for a personalized quotation.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="bg-gold hover:bg-gold-light text-burgundy-deep font-semibold">
                    Request a Quote
                  </Button>
                </Link>
                <Link to="/booking">
                  <Button size="lg" variant="outline" className="border-white/40 text-white bg-transparent hover:bg-white hover:text-burgundy-deep">
                    Book Online
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};